import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Chip,
  Combobox,
  createTheme as createMantineTheme,
  Flex,
  Group,
  Highlight,
  InputLabel,
  MultiSelect,
  NumberInput,
  Pill,
  PillsInput,
  Select,
  Skeleton,
  Stack,
  Stepper,
  Tabs,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Timeline,
  Title,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput, DateTimePicker } from "@mantine/dates";

import { defaultWhitelabelTheme, whitelabelThemes } from "@/styles/whitelabelThemes";

import type { WhitelabelBrandTheme } from "@/configs/whitelabel";

const fontFamily = "Work Sans"; // alternative: Montserrat, Roboto, DM Sans, Rubik

export const createTheme = (themeKey?: WhitelabelBrandTheme) => {
  const whitelabelTheme = themeKey ? whitelabelThemes[themeKey] : defaultWhitelabelTheme;

  return createMantineTheme({
    primaryColor: "primary",
    primaryShade: {
      light: 6,
      dark: 6,
    },
    colors: whitelabelTheme.colors,
    autoContrast: true,
    luminanceThreshold: 0.5,
    respectReducedMotion: true,
    cursorType: "pointer",
    defaultRadius: "sm",
    focusRing: "auto",
    fontSmoothing: true,

    fontWeights: {
      medium: "500",
    },

    defaultGradient: {
      deg: 45,
      from: "primary",
      to: "secondary",
    },

    breakpoints: {
      lg: "75em",
      md: "62em",
      sm: "48em",
      xl: "88em",
      xs: "36em",
    },
    other: whitelabelTheme.other,

    fontFamily,
    headings: {
      fontFamily,
      sizes: {
        h1: {
          fontSize: "calc(1.8rem * var(--mantine-scale))",
          lineHeight: "1.3",
        },
        h2: {
          fontSize: "calc(1.5rem * var(--mantine-scale))",
          lineHeight: "1.35",
        },
        h3: {
          fontSize: "calc(1.3rem * var(--mantine-scale))",
          lineHeight: "1.4",
        },
        h4: {
          fontSize: "calc(1.2rem * var(--mantine-scale))",
          lineHeight: "1.45",
        },
        h5: {
          fontSize: "calc(1.1rem * var(--mantine-scale))",
          lineHeight: "1.5",
        },
        h6: {
          fontSize: "calc(0.9rem * var(--mantine-scale))",
          lineHeight: "1.5",
        },
      },
    },

    components: {
      Badge: Badge.extend({
        defaultProps: {
          p: "0 var(--badge-padding-x-xs)",
        },
      }),
      Text: Text.extend({
        defaultProps: {
          size: "sm",
          component: "div",
        },
      }),
      Title: Title.extend({
        defaultProps: {
          textWrap: "balance",
        },
      }),
      Button: Button.extend({
        defaultProps: {
          radius: "md",
          size: "xs",
        },
      }),
      Group: Group.extend({
        defaultProps: {
          preventGrowOverflow: false,
          gap: "sm",
          align: "center",
        },
      }),
      Stack: Stack.extend({
        defaultProps: {
          gap: "sm",
        },
      }),
      Flex: Flex.extend({
        defaultProps: {
          gap: "sm",
        },
      }),
      Card: Card.extend({
        defaultProps: {
          p: {
            base: "sm",
            sm: "md",
          },
          shadow: "sm",
          radius: "md",
          withBorder: true,
        },
      }),
      Chip: Chip.extend({
        defaultProps: {
          variant: "light",
          size: "sm",
          defaultChecked: true,
          checked: true,
          readOnly: true,
        },
      }),
      Tooltip: Tooltip.extend({
        defaultProps: {
          openDelay: 300,
          withArrow: true,
          multiline: true,
        },
      }),
      ThemeIcon: ThemeIcon.extend({
        defaultProps: {
          radius: "xl",
        },
      }),
      Timeline: Timeline.extend({
        defaultProps: {
          bulletSize: 24,
          lineWidth: 3,
        },
      }),
      TimelineItem: Timeline.Item.extend({
        defaultProps: {
          styles: {
            itemTitle: {
              fontSize: "var(--mantine-font-size-sm)",
              color: "var(--mantine-color-dimmed)",
            },
          },
        },
      }),
      ComboboxDropdown: Combobox.Dropdown.extend({
        defaultProps: {
          styles: {
            dropdown: {
              boxShadow: "var(--mantine-shadow-xl)",
            },
          },
        },
      }),
      Anchor: Anchor.extend({
        defaultProps: {
          underline: "hover",
          fw: "bold",
          mx: "xs",
        },
      }),
      ActionIcon: ActionIcon.extend({
        defaultProps: {
          variant: "subtle",
          radius: "xl",
          size: "sm",
        },
      }),
      PillsInput: PillsInput.extend({
        styles: {
          label: {
            fontSize: "var(--mantine-font-size-xs)",
          },
        },
      }),
      InputLabel: InputLabel.extend({
        defaultProps: {
          fz: "var(--mantine-font-size-xs)",
          style: {
            wordBreak: "keep-all",
          },
        },
      }),
      Select: Select.extend({
        styles: {
          label: {
            fontSize: "var(--mantine-font-size-xs)",
          },
        },
      }),
      MultiSelect: MultiSelect.extend({
        styles: {
          label: {
            fontSize: "var(--mantine-font-size-xs)",
          },
        },
      }),
      TextInput: TextInput.extend({
        styles: {
          label: {
            fontSize: "var(--mantine-font-size-xs)",
          },
        },
      }),
      NumberInput: NumberInput.extend({
        styles: {
          label: {
            fontSize: "var(--mantine-font-size-xs)",
          },
        },
      }),
      DatePickerInput: DatePickerInput.extend({
        styles: {
          label: {
            fontSize: "var(--mantine-font-size-xs)",
          },
        },
      }),
      DateTimePicker: DateTimePicker.extend({
        styles: {
          label: {
            fontSize: "var(--mantine-font-size-xs)",
          },
        },
      }),
      Textarea: Textarea.extend({
        defaultProps: {
          autosize: true,
          resize: "vertical",
          minRows: 1,
        },
        styles: {
          label: {
            fontSize: "var(--mantine-font-size-xs)",
          },
        },
      }),
      Skeleton: Skeleton.extend({
        defaultProps: {
          h: "sm",
        },
      }),
      Stepper: Stepper.extend({
        defaultProps: {
          size: "sm",
          iconSize: 28,
        },
      }),
      Pill: Pill.extend({
        styles: {
          root: {
            border: "1px solid var(--mantine-color-default-border)",
          },
          label: {
            display: "inline-flex",
          },
        },
      }),
      Highlight: Highlight.extend({
        defaultProps: {
          color: "orange",
        },
      }),
      Tabs: Tabs.extend({
        defaultProps: {
          keepMounted: false,
        },
      }),
    },
  });
};

export default createTheme;
